'use client';

import { ChangeEvent, ElementType, ReactNode, useState } from 'react';
import MaskedInput from 'react-text-mask';
import { ReactComponent as Eye } from '@assets/icons/eye.svg';
import { ReactComponent as EyeSlash } from '@assets/icons/eye-slash.svg';
import { ReactComponent as CloseIcon } from '@assets/icons/xmark.svg';
import { anyCharacterMask } from 'constant';
import { FormikErrors } from 'formik';

import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  useTheme
} from '@chakra-ui/react';

interface IconTextFieldProps extends Omit<InputProps, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
  error?:
    | string
    | string[]
    | FormikErrors<Record<string, unknown>>
    | FormikErrors<Record<string, unknown>>[];
  type?: string;
  leftIcon?: ReactNode;
  iconBg?: string;
  guide?: boolean;
  mask?: false | (string | RegExp)[];
  showReset?: boolean;
  closeIconStyles?: { [key: string]: string | number };
  iconBoxStyles?: { [key: string]: string | number };
}

const IconTextField = ({
  value,
  onChange,
  label,
  error,
  variant,
  width,
  leftIcon,
  iconBg,
  mask,
  type = 'text',
  helperText = '',
  guide = false,
  showReset = false,
  closeIconStyles = {},
  iconBoxStyles = {},
  ...inputProps
}: IconTextFieldProps) => {
  const { colors } = useTheme();
  const [secure, setSecure] = useState(true);

  const inputMask = mask || anyCharacterMask;
  const isResetButton = !!value && showReset;
  const isSecureInput = type === 'password';
  const fieldType = isSecureInput && !secure ? 'text' : type;
  const isPasswordDataVisible = isSecureInput && fieldType === 'text';
  const inputType = fieldType !== 'password' ? 'text' : fieldType;

  const isDoubleIcon = isSecureInput && isResetButton;
  const inputPaddingRight = isDoubleIcon ? '90px' : '40px';
  const visibilityIconPosition = isDoubleIcon ? '50px' : '5px';
  const visibilityIcon = isPasswordDataVisible ? (
    <EyeSlash fill={colors.gray[250]} width={17} height={17} />
  ) : (
    <Eye fill={colors.gray[250]} width={17} height={17} />
  );

  const toggleSecure = () => setSecure(!secure);

  const getInputMask = (inputValue: string) => {
    const isNumberInput = type === 'number';
    const digits = inputValue.match(/\d/g) || [];
    const onlyNumbers = digits.map(() => /\d/);

    return isNumberInput && !mask ? onlyNumbers : inputMask;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleReset = () => {
    onChange('');
  };

  return (
    <FormControl
      isInvalid={Boolean(error)}
      position='relative'
      width={width}
      border='1px solid'
      borderColor={error ? 'red.500' : 'transparent'}
      borderLeftRadius='6px'
    >
      {label && (
        <FormLabel fontSize='sm' color='gray.800'>
          {label}
        </FormLabel>
      )}

      <InputGroup>
        {leftIcon && (
          <InputLeftElement
            height='60px'
            width='60px'
            borderLeftRadius='5px'
            pointerEvents='none'
            bg={iconBg}
            sx={{
              ...iconBoxStyles
            }}
          >
            {leftIcon}
          </InputLeftElement>
        )}
        {!value && (
          <FormHelperText
            display={{ base: 'none', md: 'block' }}
            textAlign='center'
            width='48%'
            fontSize='11px'
            mt={0}
            color='#8b8b8b'
            position='absolute'
            top='15px'
            right='16px'
            zIndex={99}
          >
            {helperText}
          </FormHelperText>
        )}
        <Input
          as={MaskedInput as ElementType}
          mask={getInputMask}
          value={value}
          variant='auth'
          onChange={handleChange}
          pr={inputPaddingRight}
          guide={guide}
          type={inputType}
          {...inputProps}
        />

        {isSecureInput && (
          <Box position='absolute' right={visibilityIconPosition} height='100%'>
            {value && (
              <IconButton
                aria-label='Show password'
                height='100%'
                borderRadius={0}
                icon={visibilityIcon}
                variant='ghost'
                onClick={toggleSecure}
                _hover={{
                  '& svg': {
                    fill: 'gray.400'
                  }
                }}
              />
            )}
          </Box>
        )}

        {isResetButton && (
          <InputRightElement height='100%' right='15px'>
            <IconButton
              aria-label='Clear value'
              icon={
                <CloseIcon fill={colors.gray[250]} width={36} height={36} />
              }
              variant='ghost'
              onClick={handleReset}
              height='100%'
              color='gray.700'
              borderRadius={0}
              sx={{
                '& svg': closeIconStyles
              }}
              _hover={{
                bg: 'transparent',
                color: 'gray.800',
                '& svg': {
                  fill: 'gray.400'
                }
              }}
            />
          </InputRightElement>
        )}
      </InputGroup>
    </FormControl>
  );
};

export default IconTextField;
